import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Truck, 
  Box, 
  UserCircle, 
  FileText, 
  ClipboardList, 
  GitBranch, 
  LogOut, 
  UserCog, 
  Factory, 
  Building, 
  ChevronLeft, 
  ChevronRight, 
  Briefcase, 
  UserCheck, 
  MapPin, 
  Receipt, 
  CreditCard,
  BarChart3,
  ExternalLink,
  DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const FINANZAS_SUBITEMS = [];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/avatar`).then(res => setAvatar(res.data.avatar)).catch(() => {});
  }, [location.pathname]);

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['MASTER', 'ADMIN', 'TRANSPORTISTA'] },
    { path: '/users', icon: Users, label: 'Usuarios', roles: ['MASTER', 'ADMIN'] },
    { path: '/importadoras', icon: Factory, label: 'Importadores', roles: ['MASTER'] },
    { path: '/clients', icon: UserCheck, label: 'Clientes', roles: ['MASTER'] },
    { path: '/businesses', icon: Briefcase, label: 'Negocios', roles: ['MASTER'] },
    { path: '/transportistas', icon: Building, label: 'Transportistas', roles: ['MASTER', 'ADMIN', 'TRANSPORTISTA'] },
    { path: '/dispatches', icon: FileText, label: 'Despachos', roles: ['MASTER', 'ADMIN', 'TRANSPORTISTA'] },
    { path: '/liquidaciones', icon: Receipt, label: 'Liquidaciones', roles: ['MASTER', 'ADMIN', 'TRANSPORTISTA', 'CHOFER'] },
    { path: '/pagos', icon: CreditCard, label: 'Pagos', roles: ['MASTER', 'ADMIN'] },
    { path: '/cuentas-por-cobrar', icon: DollarSign, label: 'Cuentas por Cobrar', roles: ['MASTER'] },
    { path: '/proveedores', icon: UserCheck, label: 'Proveedores', roles: ['MASTER'] },
    { path: '/inventario', icon: Box, label: 'Inventario', roles: ['MASTER', 'ADMIN'] },
    { path: '/driver-view', icon: MapPin, label: 'Mi Viaje', roles: ['CHOFER'] },
    { path: '/assignment', icon: GitBranch, label: 'Asignaciones', roles: ['ADMIN'] },
    { path: '/audit-logs', icon: ClipboardList, label: 'Auditoría', roles: ['MASTER', 'ADMIN'] },
  ];

  // Obtener rol efectivo (considerando simulación para DESARROLLO)
  const isDev = user?.role === 'DESARROLLO';
  const isSimulating = isDev && user?.simulated_role;
  const effectiveRole = isSimulating ? user.simulated_role : user?.role;
  
  // Si está simulando un rol, mostrar exactamente lo que ve ese rol
  // Si es DESARROLLO sin simulación, mostrar todo para poder navegar
  const filteredMenuItems = menuItems.filter(item => {
    if (isSimulating) {
      return item.roles.includes(effectiveRole);
    }
    if (isDev) {
      return true; // Modo desarrollo puro: ver todo
    }
    return item.roles.includes(effectiveRole);
  });
  
  const showFinanzas = isSimulating 
    ? ['MASTER', 'ADMIN'].includes(effectiveRole)
    : (isDev || ['MASTER', 'ADMIN'].includes(effectiveRole));
  
  const initials = getInitials(user?.full_name || user?.email);
  const displayName = user?.full_name?.split(' ')[0]?.toLowerCase() || user?.email?.split('@')[0] || '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenFinApp = () => {
    window.open('/finanzas', '_blank', 'noopener,noreferrer');
  };

  // Insert finanzas in correct position (after pagos)
  const pagosIdx = filteredMenuItems.findIndex(i => i.path === '/pagos');
  const itemsBefore = pagosIdx >= 0 ? filteredMenuItems.slice(0, pagosIdx + 1) : filteredMenuItems;
  const itemsAfter  = pagosIdx >= 0 ? filteredMenuItems.slice(pagosIdx + 1) : [];

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        data-testid={`nav-${item.label.toLowerCase()}`}
        title={isCollapsed ? item.label : ''}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm transition-all duration-200 ${
          isActive
            ? 'bg-white/10 text-white border-l-2 border-primary'
            : 'text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
        } ${isCollapsed ? 'justify-center px-0' : ''}`}
      >
        <Icon size={16} />
        {!isCollapsed && <span className="text-xs font-medium">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside
        className={`${isCollapsed ? 'w-16' : 'w-56'} bg-slate-900 text-white fixed h-full flex flex-col transition-all duration-300 z-50`}
        data-testid="sidebar"
      >
        {/* Title + User Info */}
        <div className={`border-b border-white/10 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <img src="/images/logo-cig.png" alt="CIG" className="h-8 w-8 object-contain" />
              <div
                className="w-8 h-8 rounded-full bg-primary/80 flex items-center justify-center text-[10px] font-bold cursor-pointer hover:bg-primary transition-colors overflow-hidden"
                onClick={() => navigate('/profile')}
                title={user?.full_name}
                data-testid="sidebar-avatar-collapsed"
              >
                {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : initials}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <img src="/images/logo-cig.png" alt="CIG Logo" className="h-10 w-10 object-contain" data-testid="sidebar-logo" />
                <div>
                  <h1 className="text-sm font-heading font-bold tracking-tight text-white" data-testid="sidebar-title">CIG</h1>
                  <p className="text-[9px] text-slate-400 leading-tight">Control de Importaciones<br/>Graneleras</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-full bg-primary/80 flex items-center justify-center text-xs font-bold flex-shrink-0 cursor-pointer hover:bg-primary transition-colors overflow-hidden"
                  onClick={() => navigate('/profile')}
                  data-testid="sidebar-avatar"
                >
                  {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : initials}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white truncate" data-testid="sidebar-username">{displayName}</p>
                  <div className="flex items-center gap-1">
                    <p className="text-[10px] text-slate-400 font-mono uppercase" data-testid="sidebar-role">{effectiveRole}</p>
                    {isDev && <span className="text-[8px] bg-purple-500 text-white px-1 rounded">DEV</span>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto" data-testid="navigation">
          {itemsBefore.map(renderMenuItem)}

          {/* ── FinApp — abre en nueva ventana ── */}
          {showFinanzas && (
            <button
              onClick={handleOpenFinApp}
              data-testid="nav-finapp"
              title={isCollapsed ? 'FinApp' : ''}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm transition-all duration-200 text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent ${isCollapsed ? 'justify-center px-0' : ''}`}
            >
              <BarChart3 size={16} />
              {!isCollapsed && (
                <>
                  <span className="text-xs font-medium flex-1 text-left">FinApp</span>
                  <ExternalLink size={11} className="text-slate-600 flex-shrink-0" />
                </>
              )}
            </button>
          )}

          {itemsAfter.map(renderMenuItem)}
        </nav>

        {/* Footer */}
        <div className="p-2 border-t border-white/10 space-y-0.5">
          <button
            onClick={() => navigate('/profile')}
            data-testid="nav-profile"
            title={isCollapsed ? 'Perfil' : ''}
            className={`w-full flex items-center gap-2 px-3 py-2 text-slate-300 hover:bg-white/5 hover:text-white rounded-sm transition-all duration-200 ${isCollapsed ? 'justify-center px-0' : ''}`}
          >
            <UserCog size={16} />
            {!isCollapsed && <span className="text-xs font-medium">Perfil</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title={isCollapsed ? 'Cerrar Sesión' : ''}
            className={`w-full flex items-center gap-2 px-3 py-2 text-slate-300 hover:bg-red-500/10 hover:text-red-400 rounded-sm transition-all duration-200 ${isCollapsed ? 'justify-center px-0' : ''}`}
          >
            <LogOut size={16} />
            {!isCollapsed && <span className="text-xs font-medium">Cerrar Sesión</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-slate-800 hover:bg-slate-700 text-white rounded-full p-1 shadow-md border border-slate-600 transition-colors"
          title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      <main className={`flex-1 ${isCollapsed ? 'ml-16' : 'ml-56'} transition-all duration-300 relative z-10`}>
        {/* Marca de agua */}
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginLeft: isCollapsed ? '4rem' : '14rem' }}>
          <img src="/images/logo-cig.png" alt="" className="w-[500px] h-auto opacity-[0.04]" />
        </div>
        {/* Contenido principal */}
        <div className="relative z-10 p-4 md:p-6 min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
